import { useEffect, useRef } from 'react';
import { isAtMostMobile, withBreakpointRange } from '@optimus-web/breakpoints';
import { handleRefSplit } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { Checkbox } from '../Checkbox';
import { Icon } from '../Icon';
import { Stack } from '../Stack';
import { Typography } from '../typography';
import { getBreakpointRangeValueFallback } from '../utils';
import {
    DropdownItemContainer,
    DropdownItemSelectedContainer,
    Label,
    LeftDetailContainer,
    RightDetailContainer,
} from './style';
import { BaseDropdownItemProps } from './types';

const DropdownItemComponent = ({
    label,
    description,
    breakpointRange,
    disabled,
    selected,
    indeterminate,
    onClick,
    onAfterClick,
    value,
    leftDetail,
    rightDetail,
    multiselect,
    focused,
    shouldScrollIntoView,
    ariaLabel,
    id,
    onMouseEnter,
    innerRef,
    variant = 'primary',
    readOnly,
    ...other
}: BaseDropdownItemProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (focused && shouldScrollIntoView && containerRef.current) {
            containerRef.current.scrollIntoView(false);
        }
    }, [focused, shouldScrollIntoView]);

    const handleClick = (e: React.MouseEvent) => {
        onClick?.(e, value);
        onAfterClick?.(e, value);
    };

    const isMobile = isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange));

    return (
        <DropdownItemContainer
            {...other}
            disabled={disabled}
            selected={selected}
            onClick={!disabled ? handleClick : undefined}
            spacing="150"
            tabletUp={{
                spacing: '100',
            }}
            itemAlignment="center"
            focused={focused}
            ref={handleRefSplit({ ownRef: containerRef, forwardedRef: innerRef })}
            aria-label={ariaLabel}
            value={value}
            id={id}
            role="option"
            tabIndex={-1}
            aria-selected={!disabled && focused}
            onMouseEnter={!disabled ? onMouseEnter : undefined}
            data-test-dropdown-item
            data-list-container
            readOnly={readOnly}
        >
            {multiselect ? (
                <Checkbox
                    as={Stack}
                    tabIndex={-1}
                    checked={!!selected}
                    value={value}
                    name={id}
                    disabled={disabled}
                    size={isMobile ? 'large' : 'medium'}
                    indeterminate={indeterminate}
                />
            ) : null}
            {!multiselect && leftDetail ? <LeftDetailContainer>{leftDetail}</LeftDetailContainer> : null}
            <Stack
                vertical
                fill
                phoneOnly={{
                    spacing: '25',
                }}
            >
                <Label as="div" isMobile={isMobile} data-test-primary-text variant={variant}>
                    {label}
                </Label>

                {description ? (
                    <Typography textStyle={isMobile ? 'bodyMedium' : 'bodySmall'} data-test-secondary-text as="div">
                        {description}
                    </Typography>
                ) : null}
            </Stack>
            {rightDetail ? <RightDetailContainer>{rightDetail}</RightDetailContainer> : null}
            {selected && !multiselect ? (
                <DropdownItemSelectedContainer>
                    <Icon value={IconName.Done} size="small" />
                </DropdownItemSelectedContainer>
            ) : null}
        </DropdownItemContainer>
    );
};

export const DropdownItem = withBreakpointRange(DropdownItemComponent);
